/**
 * The FT3D scene and combat strip are shared input surfaces. Exactly one feature may interpret their
 * next click at a time; otherwise a stale initiative/GM picker can steal a click from a newly armed
 * ability (or vice versa). State lives on window because each consumer is emitted as a separate bundle.
 */
export type Ft3dInteractionOwner =
    | 'ruler'
    | 'ping'
    | 'ability-targeting'
    | 'aoe-placement'
    | 'teleport-placement'
    | 'latch-targeting'
    | 'hunker-preview'
    | 'initiative-pick'
    | 'turn-jump'
    | 'gm-target-action';

interface Ft3dInteractionModeState {
    owner: Ft3dInteractionOwner;
    cancel: () => void;
}

type InteractionModeWindow = Window & {
    __ft3dInteractionMode?: Ft3dInteractionModeState | null;
};

function host(): InteractionModeWindow {
    return window as InteractionModeWindow;
}

function publish(owner: Ft3dInteractionOwner | null): void {
    window.dispatchEvent(new CustomEvent('ft:interaction-mode-changed', { detail: { owner } }));
}

/** Claim the shared FT3D click surface, synchronously cancelling its previous owner first. */
export function claimFt3dInteractionMode(owner: Ft3dInteractionOwner, cancel: () => void): void {
    const w = host();
    const previous = w.__ft3dInteractionMode ?? null;
    if (previous?.owner === owner) {
        w.__ft3dInteractionMode = { owner, cancel };
        return;
    }

    // Clear before invoking the callback. The previous owner's normal release() then becomes a no-op,
    // which avoids re-entrant cancellation loops (notably AOE cancel -> broad cleanup -> AOE cancel).
    w.__ft3dInteractionMode = null;
    if (previous) {
        try { previous.cancel(); }
        catch (error) { console.error('claimFt3dInteractionMode: previous owner cancel failed', error); }
    }
    w.__ft3dInteractionMode = { owner, cancel };
    publish(owner);
}

/** Release only the caller's ownership; an older teardown cannot clear a newer interaction. */
export function releaseFt3dInteractionMode(owner: Ft3dInteractionOwner): void {
    const w = host();
    if (w.__ft3dInteractionMode?.owner !== owner) return;
    w.__ft3dInteractionMode = null;
    publish(null);
}

export function currentFt3dInteractionMode(): Ft3dInteractionOwner | null {
    return host().__ft3dInteractionMode?.owner ?? null;
}

declare global {
    interface Window {
        ftClaimInteractionMode?: typeof claimFt3dInteractionMode;
        ftReleaseInteractionMode?: typeof releaseFt3dInteractionMode;
        ftCurrentInteractionMode?: typeof currentFt3dInteractionMode;
    }
}

// Diagnostics and legacy non-module callers may use these without depending on a bundle's internals.
window.ftClaimInteractionMode = claimFt3dInteractionMode;
window.ftReleaseInteractionMode = releaseFt3dInteractionMode;
window.ftCurrentInteractionMode = currentFt3dInteractionMode;
